import React, { useContext, forwardRef, useImperativeHandle } from "react";
import { useHistory } from "react-router-dom";
import { Container } from "../styled.ts";
import { Typography } from "../typography/index.tsx";
import { subscriberContext } from "../../context/subscriberContext.tsx";
import { API_URLS } from "../../utils/apiConfig.ts";
import { getCountryCodeFromSubscriber } from "../../utils/countryUtils.ts";

interface PlanDetails {
  name: string;
  billingCycle: "Monthly" | "Yearly";
  couponCode?: string;
}

interface OrderResponse {
  id: string;
  amount: number;
  currency: string;
  razorpayKey: string;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface PaymentButtonRef {
  openPayment: () => void;
}

/**
 * "gradient" is the primary CTA on light cards, "light" the white pill used on the
 * gradient (recommended) card. Defaults to "light" so existing callers are unchanged.
 */
type PaymentButtonTone = "light" | "gradient";

interface PaymentButtonProps {
  planDetails: PlanDetails;
  tone?: PaymentButtonTone;
  label?: string;
}

const PaymentButton = forwardRef<PaymentButtonRef, PaymentButtonProps>(function PaymentButton({ planDetails, tone = "light", label }, ref) {
  const history = useHistory();
  const { subscriberDetails, setSubscriberDetails } = useContext(subscriberContext);

  const verifyPayment = async (response: any) => {
    let headersList = {
      "Content-Type": "application/json",
    };
    let bodyContent = JSON.stringify(response);
    fetch(API_URLS.PAY_VERIFY, {
      method: "POST",
      body: bodyContent,
      headers: headersList,
    });
  };

  const handlePayment = async () => {
    if (!subscriberDetails) {
      history.push({ pathname: "/", search: "redirectTo=subscription-plans" });
      return;
    }
    if (!window || !window?.Razorpay) {
      console.error("Razor pay util failed to load due to multiple access");
      return;
    }
    try {
      const orderResp: OrderResponse = await fetch(API_URLS.PAY_CREATE_ORDER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: subscriberDetails.phoneNumber,
          planName: planDetails.name,
          billingCycle: planDetails.billingCycle,
          countryCode: "91", // backend defaults will apply if needed
          couponCode: planDetails.couponCode || null,
        }),
      }).then((res) => res.json());

      const options = {
        key: orderResp.razorpayKey,
        amount: orderResp.amount,
        currency: orderResp.currency,
        name: "KONSRI TECHNOLOGIES PRIVATE LIMITED",
        description: `Subscription for ${planDetails.name} (${planDetails.billingCycle})`,
        order_id: orderResp.id,
        handler: (success: any) => {
          verifyPayment({ success });
          setSubscriberDetails({
            ...subscriberDetails,
            subscribePlan: planDetails.name,
            subscriptionTerm: planDetails.billingCycle,
            paymentDate: new Date(),
            subscriptionExpiryDate:
              planDetails.billingCycle === "Monthly"
                ? new Date(new Date().setMonth(new Date().getMonth() + 1))
                : new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
          });
          history.push({ pathname: "/watch-list", search: `planupdateto=${planDetails.name}` });
        },
        prefill: {
          contact: subscriberDetails.phoneNumber,
          name: subscriberDetails.name,
        },
        theme: { color: "#F37254" },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
      razorpayInstance.on("payment.failed", verifyPayment);
    } catch (e) {
      console.log(e);
    }
  };

  useImperativeHandle(ref, () => ({ openPayment: handlePayment }), [subscriberDetails, planDetails]);

  const isGradient = tone === "gradient";

  return (
    <Container
      padding="14px 20px"
      width="100%"
      background={
        isGradient
          ? "linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"
          : "#fff"
      }
      radius="100px"
      justifyContent="center"
      alignItems="center"
      role="button"
      tabIndex={0}
      style={{
        cursor: "pointer",
        boxSizing: "border-box",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        boxShadow: isGradient
          ? "0 6px 18px rgba(83, 149, 255, 0.28)"
          : "0 2px 8px rgba(32, 32, 32, 0.08)",
      }}
      border={isGradient ? "solid 1px transparent" : "solid 1px #eee"}
      onClick={handlePayment}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handlePayment();
        }
      }}
    >
      <Typography
        fontFamily="Neue Montreal"
        fontSize={"16px"}
        lineHeight={"22.4px"}
        fontWeight={500}
        cursor="pointer"
        color={isGradient ? "#fff" : "#202020"}
      >
        {label || (!!subscriberDetails ? "Subscribe Now" : "Login to Subscribe")}
      </Typography>
    </Container>
  );
});

export default PaymentButton;
